import { shallowEqual } from 'react-redux';
import { usePanelIdentity } from '../common';
import { TPanelName } from '../types';
import { TPanelParameter, useParametersSelector } from './parametersStore';
import { useDashboardContext } from '../dashboard';

export type TParametersSelector<Selected> = (
  parameters: Record<string, TPanelParameter>,
) => Selected;

/**
 // eslint-disable-next-line comment-length/limit-multi-line-comments
 * Este hook permite seleccionar parámetros de un panel. Cada vez que los
 * parámetros del panel sean actualizados, se llamará a la función selector y en
 * caso de que la selección actual de parámetros difiera de la anterior, se
 * provocará un renderizado en el componente que lo utiliza.
 *
 * Si se quiere obtener los parámetros del panel en el que se está trabajando,
 * el segundo parámetro es innecesario. En caso de querer obtener los
 * parámetros de otro panel, se puede pasar su nombre como segundo parámetro y
 * la selección se hará de acuerdo a los parámetros de éste.
 *
 * Es importante ver que es imposible prever qué parámetros tendrá cada panel,
 * por ello el tipo entregado al selector es Record, que puede entenderse como
 * un mapa de entradas con clave string y valor TPanelParameter = string |
 * boolean | number.
 */
export function usePanelParametersSelector<Selected>(
  selector: TParametersSelector<Selected>,
  panelName?: TPanelName,
) {
  const { panelId } = usePanelIdentity();
  const dashboard = useDashboardContext();
  const id =
    panelName !== undefined
      ? dashboard.getPanelByName(panelName)?.id ?? ''
      : panelId;

  return useParametersSelector((global) => {
    const currentParams = global.panelParameters[id];
    return selector(currentParams ?? {});
  }, shallowEqual);
}
